<template>
    <div>
        <!-- Document Section -->
        <div class="mt-2 md:mt-6">
            <h2 class="font-bold uppercase tracking-wider">{{ $t("documents.title") }}</h2>
            <table class="w-full divide-y divide-gray-200 mt-2 md:mt-4">
                <thead class="bg-minplan-lightgreybeige">
                    <tr>
                        <th
                            scope="col"
                            class="px-1 lg:px-3 py-2 lg:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("documents.document") }}
                        </th>
                        <th
                            scope="col"
                            class="hidden lg:table-cell px-1 lg:px-3 py-2 lg:py-3 text-left text-xs font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("documents.date") }}
                        </th>
                        <th
                            scope="col"
                            class="hidden md:table-cell px-1 lg:px-3 py-2 lg:py-3 text-center text-xs font-medium text-minplan-beige uppercase tracking-wider"
                        >
                            {{ $t("documents.version") }}
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-if="documents.items.length === 0">
                        <td
                            colspan="7"
                            class="p-3 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                            {{ $t("documents.emptyDocuments") }}
                        </td>
                    </tr>
                    <tr v-for="document in documents.items" :key="document.uuid">
                        <td
                            class="px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                            {{ document.name }}
                            <button
                                type="button"
                                v-if="document.comment"
                                @click="showComments(document)"
                                class="inline-block bg-minplan-beige rounded-full px-2 py-1"
                            >
                                <ChatAltIcon
                                    class="h-5 w-5 text-minplan-darkbeige inline-block"
                                />
                            </button>
                            <div v-if="document.signature_status" class="mt-1 flex flex-col gap-1 text-xs font-normal text-gray-500">
                                <span title="Digital Signature Progress" v-if="document.signature_status.total > 0">
                                    Signaturen: {{ document.signature_status.completed }} / {{ document.signature_status.total }}
                                </span>
                                <span v-if="document.signature_status.pending_names.length > 0" class="text-xs text-red-500 whitespace-normal">
                                    Ausstehend: {{ document.signature_status.pending_names.join(', ') }}
                                </span>
                            </div>
                        </td>
                        <td
                            class="hidden lg:table-cell px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-sm font-medium text-gray-900"
                        >
                            {{ document.createdAt }}
                        </td>
                        <td
                            class="hidden md:table-cell px-1 lg:px-3 py-2 lg:py-3 whitespace-nowrap text-center text-sm font-medium text-gray-900"
                        >
                            <button type="button">
                                {{ document.version }}
                                <InformationCircleIcon
                                    @click="$inertia.visitInModal(document.historyLink)"
                                    class="-mt-1 ml-1 h-5 w-5 inline text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                />
                            </button>
                        </td>
                        <td class="text-right">
                            <button v-if="document.filename && isViewable(document.filename)" type="button" @click="viewFile(document.viewLink, document.filename)" title="View" class="ml-3">
                                <EyeIcon class="h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige" />
                            </button>
                            <button
                                v-if="document.editLink"
                                type="button"
                                @click="$inertia.visitInModal(document.editLink)"
                            >
                                <PencilIcon
                                    class="ml-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                />
                            </button>
                            <button
                                v-if="document.replaceLink"
                                type="button"
                                @click="$inertia.visitInModal(document.replaceLink)"
                            >
                                <RefreshIcon
                                    class="ml-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                />
                            </button>
                            <button type="button" @click="download(document)">
                                <CloudDownloadIcon
                                    class="ml-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                />
                            </button>
                            <button
                                v-if="document.deleteLink"
                                type="button"
                                @click="$inertia.visitInModal(document.deleteLink)"
                            >
                                <TrashIcon
                                    class="ml-3 h-5 w-5 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                                />
                            </button>
                            <button
                                v-if="document.signature_status && (document.signature_status.total > 0 || document.signature_status.is_self_sign === 'Yes')"
                                type="button"
                                @click="showSignatureHistory(document)"
                                title="Signature History"
                            >
                                <svg class="ml-3 h-5 w-5 text-gray-500 hover:text-gray-700 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </button>
                            <button
                                v-if="document.currentUserNeedsToSign"
                                type="button"
                                @click="openSignModal(document)"
                                title="Sign document"
                            >
                                <PencilAltIcon
                                    class="ml-3 h-5 w-5 text-minplan-blue hover:text-minplan-darkgreybeige"
                                />
                            </button>
                            <button
                                v-if="document.can_admin_self_sign"
                                type="button"
                                :disabled="loadingAdminSignUuid === document.uuid"
                                @click="adminSelfSign(document)"
                                class="ml-3 inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded shadow-sm hover:bg-blue-200 transition disabled:opacity-50"
                                title="Admin Self Sign"
                            >
                                <span v-if="loadingAdminSignUuid === document.uuid" class="flex items-center gap-1">
                                    <svg class="animate-spin h-3 w-3 text-blue-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                    Loading...
                                </span>
                                <span v-else>Admin Sign</span>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>

            <!-- Upload Document Button -->
            <div class="flex justify-end mt-3">
                <button
                    type="button"
                    v-if="documents.createLink"
                    @click="openCreateModal"
                    class="ml-3 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-minplan-greybeige hover:bg-minplan-lightgreybeige focus:outline-none uppercase"
                >
                    <CloudUploadIcon class="-ml-1 mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                    <span class="inline font-bold">{{ $t("documents.new") }}</span>
                </button>
            </div>
        </div>

        <!-- Signing Modal -->
        <jet-dialog-modal :show="!!signingUrl" @close="signingUrl = null" max-width="full">
            <template #title>
                <div class="flex justify-between items-center pr-4">
                    <span>{{ $t("documents.sign") }}</span>
                    <button @click="checkBuyerCompletion" class="bg-emerald-600 text-white px-4 py-1 rounded text-sm font-bold shadow hover:bg-emerald-700 transition">
                        {{ $t("documents.finishedSigning") }}
                    </button>
                </div>
            </template>

            <template #content>
                <div class="w-full h-[75vh] border rounded overflow-hidden bg-gray-50 relative">
                    <iframe v-if="signingUrl" :src="signingUrl" class="w-full h-full" frameborder="0"></iframe>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-between w-full">
                    <p class="text-xs text-gray-500 italic pt-2">{{ $t("documents.clickGreenButton") }}</p>
                    <jet-secondary-button @click="signingUrl = null">
                        {{ $t("actions.close") }}
                    </jet-secondary-button>
                </div>
            </template>
        </jet-dialog-modal>

        <!-- Comments Modal -->
        <jet-dialog-modal :show="showModal" @close="closeModal">
            <template #title>
                {{ $t("documents.showComment") }}
            </template>

            <template #content>
                {{ currentComment }}
            </template>

            <template #footer>
                <button
                    @click="closeModal"
                    type="button"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                >
                    {{ $t("actions.close") }}
                </button>
            </template>
        </jet-dialog-modal>

        <!-- Signature History Modal -->
        <jet-dialog-modal :show="!!historyDocument" @close="historyDocument = null" max-width="2xl">
            <template #title>
                Signaturverlauf der Offerte
            </template>
            <template #content>
                <div v-if="historyDocument?.signature_status" class="overflow-x-auto">
                    <table class="min-w-full divide-y border text-sm">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Unterzeichner</th>
                                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                                <th class="px-6 py-3 text-left font-medium text-gray-500 uppercase tracking-wider">Aktion</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y">
                            <tr v-for="(buyer, idx) in historyDocument.signature_status.history" :key="'buyer-'+idx">
                                <td class="px-6 py-4 whitespace-nowrap">{{ buyer.buyer_name }}</td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span v-if="buyer.is_sign === 'Yes'" class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Unterzeichnet</span>
                                    <span v-else class="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Ausstehend</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {{ buyer.sign_at ? formatDate(buyer.sign_at) : 'N/A' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap flex gap-2">
                                    <button v-if="buyer.is_sign === 'Yes'" @click="download(historyDocument)" class="text-emerald-600 hover:text-emerald-700 font-medium text-xs border border-emerald-600 px-2 py-1 rounded">
                                        {{ $t("actions.download") }}
                                    </button>
                                    <button v-if="buyer.is_sign === 'Yes' && isViewable(historyDocument.filename)" @click="viewFile(historyDocument.viewLink, historyDocument.filename)" class="text-emerald-600 hover:text-emerald-700 font-medium text-xs border border-emerald-600 px-2 py-1 rounded">
                                        {{ $t("actions.view") }}
                                    </button>
                                    <template v-else>
                                        <button v-if="!$page.props.user.buyer_id" :disabled="resendingEmailId === buyer.buyer_id" @click="resendEmail(buyer)" class="text-minplan-blue hover:text-minplan-darkblue font-medium text-xs border border-minplan-blue px-2 py-1 rounded disabled:opacity-50">
                                            {{ resendingEmailId === buyer.buyer_id ? $t("orderModifications.pendingSignatures.sending") : $t("orderModifications.pendingSignatures.resendEmail") }}
                                        </button>
                                        <button v-if="isSignableByMe(historyDocument, buyer)" @click="openSignModal(historyDocument, buyer)" class="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 font-bold text-xs border border-emerald-600 px-2 py-1 rounded">
                                            <PencilAltIcon class="h-3 w-3" />
                                            {{ buyer.buyer_name ? $t("orderModifications.pendingSignatures.signNowFor", { name: buyer.buyer_name }) : $t("orderModifications.pendingSignatures.signNow") }}
                                        </button>
                                    </template>
                                </td>
                            </tr>
                            <tr v-if="$page.props.document_digital_sign_mode === 'On'">
                                <td class="px-6 py-4 whitespace-nowrap">Admin (Eigene Signatur)</td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span v-if="historyDocument.signature_status.is_self_sign === 'Yes'" class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Unterzeichnet</span>
                                    <span v-else class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Nicht unterzeichnet</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-gray-500">
                                    {{ historyDocument.signature_status.admin_sign_at ? formatDate(historyDocument.signature_status.admin_sign_at) : 'N/A' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <button v-if="historyDocument.signature_status.is_self_sign === 'Yes'" @click="download(historyDocument)" class="text-emerald-600 hover:text-emerald-700 font-medium text-xs border border-emerald-600 px-2 py-1 rounded">
                                        Herunterladen
                                    </button>
                                    <button v-if="historyDocument.signature_status.is_self_sign === 'Yes' && isViewable(historyDocument.filename)" @click="viewFile(historyDocument.viewLink, historyDocument.filename)" class="text-emerald-600 hover:text-emerald-700 font-medium text-xs border border-emerald-600 px-2 py-1 rounded">
                                        Ansehen
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
            <template #footer>
                <jet-secondary-button @click="historyDocument = null">
                    Schliessen
                </jet-secondary-button>
            </template>
        </jet-dialog-modal>

        <teleport to="body">
            <digital-signature-config 
                v-if="showAdminSignConfig"
                :pdf-base64="adminSignPdfBase64"
                :file-name="adminSignDoc?.name"
                :auth-user-id="$page.props.user.id"
                is-admin-sign-only
                @cancel="showAdminSignConfig = false"
                @done="finalizeAdminSign"
            />
            <file-preview-modal
                :show="previewFile !== null"
                :file-url="previewFile?.url"
                :file-name="previewFile?.name"
                @close="previewFile = null"
            />
        </teleport>
    </div>
</template>

<script>
import {
    CheckIcon,
    CloudDownloadIcon,
    CloudUploadIcon,
    InformationCircleIcon,
    PaperAirplaneIcon,
    PencilIcon,
    RefreshIcon,
    TrashIcon,
    XIcon,
    ChatAltIcon,
    PencilAltIcon,
    EyeIcon,
} from "@heroicons/vue/outline";
import { downloadFile } from "@/Shared/Utils/downloader";
import JetDialogModal from "@/Jetstream/DialogModal";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import DigitalSignatureConfig from "@/Shared/Components/Documents/Modals/DigitalSignatureConfig.vue";
import FilePreviewModal from "@/Shared/Components/FilePreviewModal.vue";
import axios from "axios";
import { defineComponent } from "vue";

export default defineComponent({
    name: "DocumentTable",
    props: ["documents"],
    data() {
        return {
            showModal: false,
            currentComment: null,
            signingUrl: null,
            signingDocUuid: null,
            signingBuyerId: null,
            signingOpenSignId: null,
            isAdminSelfSign: false,
            historyDocument: null,
            showAdminSignConfig: false,
            adminSignPdfBase64: null,
            adminSignDoc: null,
            pollingInterval: null,
            resendingEmailId: null,
            loadingAdminSignUuid: null,
            previewFile: null,
        };
    },
    watch: {
        signingUrl(newVal) {
            if (newVal) {
                this.startPolling();
            } else {
                this.stopPolling();
            }
        }
    },
    components: {
        CloudUploadIcon,
        CloudDownloadIcon,
        PencilIcon,
        TrashIcon,
        PaperAirplaneIcon,
        RefreshIcon,
        InformationCircleIcon,
        CheckIcon,
        XIcon,
        ChatAltIcon,
        JetDialogModal,
        JetSecondaryButton,
        PencilAltIcon,
        DigitalSignatureConfig,
        FilePreviewModal,
        EyeIcon,
    },
    beforeUnmount() {
        this.stopPolling();
    },
    mounted() {
        if (new URLSearchParams(window.location.search).has('autosign')) {
            const items = Array.isArray(this.documents.items) ? this.documents.items : Object.values(this.documents.items);
            const doc = items.find(d => d.currentUserNeedsToSign);
            if (doc) {
                this.openSignModal(doc);
            }
        }
    },
    methods: {
        isViewable(fileName) {
            if (!fileName) return false;
            const ext = fileName.toLowerCase().split('.').pop();
            return ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext);
        },
        viewFile(url, name) {
            const ext = name.toLowerCase().split('.').pop();
            if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) {
                window.Fancybox.show([{ src: url, type: "image" }]);
            } else {
                this.previewFile = { url, name };
            }
        },
        openCreateModal() {
            this.$inertia.visitInModal(this.documents.createLink);
        },
        download(document) {
            downloadFile(document.downloadLink, document.filename);
        },
        closeModal() {
            this.showModal = false;
            this.currentComment = null;
        },
        showComments(document) {
            this.showModal = true;
            this.currentComment = document.comment;
        },
        // Der Server sagt, welche Kaeufer-Zeilen dieses Login unterschreiben
        // darf. Frueher verglich das Frontend gegen die EINE `user.buyer_id`
        // aus den Shared Props; ein Login mit mehreren Kaeufer-Zeilen traf
        // damit hoechstens auf einer Wohnung zu. `buyer_id` kommt als String,
        // die Liste als Zahl — ohne `Number()` faende `includes` nie etwas.
        isSignableByMe(document, buyer) {
            const ids = document.signature_status?.signable_buyer_ids ?? [];

            return ids.includes(Number(buyer.buyer_id));
        },
        // Die erste noch offene Zeile, die dieses Login unterschreiben darf —
        // fuer die Aufrufe ohne ausgewaehlte Zeile (Symbol in der Zeile,
        // Direkteinstieg ueber den Parameter).
        firstSignableRow(document) {
            const ids = document.signature_status?.signable_buyer_ids ?? [];

            return (
                (document.signature_status?.history ?? []).find(
                    (row) =>
                        (row.is_sign ?? "No") === "No" &&
                        ids.includes(Number(row.buyer_id)),
                ) ?? null
            );
        },
        // Die URL kommt aus der ZEILE, nicht mehr aus `currentUserSigningUrl`:
        // bei zwei unterschreibbaren Zeilen ist ein einzelner Wert nicht mehr
        // eindeutig.
        openSignModal(document, buyer = null) {
            const row = buyer ?? this.firstSignableRow(document);

            if (row?.url) {
                this.signingUrl = row.url;
                this.signingBuyerId = Number(row.buyer_id);
                this.signingDocUuid = document.uuid;
                this.signingOpenSignId = document.digital_sign_json?.docId; 
                
                if (!this.signingOpenSignId) {
                    this.$notify({ group: "error", title: "Config Error", text: "OpenSign ID is missing in document data. Please re-send the document if possible." });
                }
            } else {
                this.$notify({ group: "error", title: "Error", text: "Signing URL not found or you don't need to sign." });
            }
        },
        async adminSelfSign(document) {
            this.loadingAdminSignUuid = document.uuid;
            try {
                this.$notify({ group: "info", title: this.$t('topics.info'), text: this.$t('documents.downloadingForSigning') });
                const res = await axios.post('/documents/digital-sign/buyer-signed-pdf', {
                    documentUuid: document.uuid
                });
                
                if (res.data?.ok && res.data?.pdf) {
                    this.adminSignDoc = document;
                    this.adminSignPdfBase64 = res.data.pdf;
                    this.showAdminSignConfig = true;
                } else {
                    this.$notify({ group: "error", title: "Error", text: res.data?.error || "Error initializing admin self sign." });
                }
            } catch (e) {
                console.error(e);
                this.$notify({ group: "error", title: "Error", text: "Failed to initialize." });
            } finally {
                this.loadingAdminSignUuid = null;
            }
        },
        async finalizeAdminSign(payload) {
            this.showAdminSignConfig = false;
            try {
                const res = await axios.post('/documents/digital-sign/complete-admin', {
                    documentUuid: this.adminSignDoc.uuid,
                    finalDocId: payload.digital_sign_json.docId
                });
                
                if (res.data?.ok) {
                    this.$inertia.reload();
                    if (window.Swal) {
                        window.Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: 'Admin signature processed successfully.',
                            timer: 2000
                        });
                    }
                } else {
                    this.$notify({ group: "error", title: "Error", text: res.data?.error || "Completion check failed." });
                }
            } catch (e) {
                console.error(e);
                this.$notify({ group: "error", title: "Error", text: "An error occurred while finalizing signature." });
            } finally {
                this.adminSignDoc = null;
                this.adminSignPdfBase64 = null;
            }
        },
        async checkBuyerCompletion(silent = false) {
            if (!this.signingDocUuid) return;
            
            try {
                let url = this.isAdminSelfSign ? '/documents/digital-sign/complete-admin' : '/documents/digital-sign/complete-buyer';
                let payload = this.isAdminSelfSign 
                    ? { documentUuid: this.signingDocUuid } 
                    : { docId: this.signingOpenSignId, documentUuid: this.signingDocUuid, buyerId: this.signingBuyerId };
                    
                const res = await axios.post(url, payload);
                
                if (res.data?.ok) {
                    this.stopPolling();
                    this.signingUrl = null;
                    this.signingDocUuid = null;
                    this.signingBuyerId = null;
                    this.signingOpenSignId = null;
                    this.isAdminSelfSign = false;
                    
                    // Reload the page to get updated document status
                    this.$inertia.reload();
                    
                    if (window.Swal) {
                        window.Swal.fire({
                            icon: 'success',
                            title: 'Success',
                            text: 'Signature processed successfully.',
                            timer: 2000
                        });
                    }
                } else {
                    if (!silent) {
                        this.$notify({ group: "error", title: "Error", text: res.data?.error || "Completion check failed." });
                    }
                }
            } catch (e) {
                if (!silent) {
                    console.error(e);
                    this.$notify({ group: "error", title: "Error", text: "An error occurred while checking completion." });
                }
            }
        },
        startPolling() {
            this.stopPolling();
            const interval = (parseInt(this.$page.props.check_sign_complete_or_not_in_second) || 2) * 1000;
            this.pollingInterval = setInterval(() => {
                this.checkBuyerCompletion(true);
            }, interval);
        },

        stopPolling() {
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
        },
        showSignatureHistory(document) {
            this.historyDocument = document;
        },
        async resendEmail(buyer) {
            this.resendingEmailId = buyer.buyer_id;
            try {
                const res = await axios.post('/documents/digital-sign/resend-email', {
                    documentUuid: this.historyDocument.uuid,
                    buyerId: buyer.buyer_id
                });
                if (res.data?.ok) {
                    this.$notify({ group: "success", title: "Erfolg", text: "Erinnerungs-E-Mail versendet." });
                } else {
                    this.$notify({ group: "error", title: "Fehler", text: res.data?.error || "E-Mail konnte nicht erneut versendet werden." });
                }
            } catch (e) {
                console.error(e);
                this.$notify({ group: "error", title: "Fehler", text: "Etwas ist schiefgelaufen." });
            } finally {
                this.resendingEmailId = null;
            }
        },
        formatDate(dateString) {
            if (!dateString) return '';
            const d = new Date(dateString);
            return d.toLocaleString();
        }
    },
});
</script>

<style scoped></style>
